import React, { useMemo } from 'react';
import { WeatherCondition } from '../../api/weather/types';
import { usePerformanceMonitor } from '../../hooks/weather/usePerformanceMonitor';
import styles from './WeatherBackground.module.scss';

interface WeatherBackgroundProps {
  weatherCondition: WeatherCondition;
  temperature?: number;
  isLoading: boolean;
  hasError?: boolean;
  isFallback?: boolean;
  errorSeverity?: 'low' | 'medium' | 'high';
}

const WeatherBackground: React.FC<WeatherBackgroundProps> = React.memo(
  ({
    weatherCondition,
    isLoading,
    hasError = false,
    isFallback = false,
    errorSeverity = 'medium',
  }) => {
    // Performance monitoring
    usePerformanceMonitor(
      { componentName: 'WeatherBackground', trackProps: true },
      [weatherCondition, isLoading, hasError, isFallback, errorSeverity]
    );

    const backgroundClass = useMemo((): string => {
      if (isLoading) return styles.loading;
      if (hasError) {
        // Different error styles based on severity
        switch (errorSeverity) {
          case 'high':
            return styles.errorHigh;
          case 'medium':
            return styles.errorMedium;
          case 'low':
            return styles.errorLow;
          default:
            return styles.error;
        }
      }
      
      // Apply fallback styling if using fallback data
      const baseClass = (() => {
        switch (weatherCondition) {
          case 'clear-day':
            return styles.clearDay;
          case 'clear-night':
            return styles.clearNight;
          case 'cloudy':
            return styles.cloudy;
          case 'partly-cloudy':
            return styles.partlyCloudy;
          case 'rain':
            return styles.rain;
          case 'snow':
            return styles.snow;
          case 'fog':
            return styles.fog;
          default:
            return styles.default;
        }
      })();
      
      return isFallback ? `${baseClass} ${styles.fallback}` : baseClass;
    }, [isLoading, hasError, errorSeverity, weatherCondition, isFallback]);

    // Memoize animated elements to prevent unnecessary re-renders
    const rainDrops = useMemo(() => {
      if (weatherCondition !== 'rain' || isLoading || hasError) return null;
      
      // Reduce animation intensity for fallback data
      const dropCount = isFallback ? 25 : 50;
      
      return (
        <div className={styles.rainDrops}>
          {Array.from({ length: dropCount }, (_, i) => (
            <div
              key={i}
              className={`${styles.rainDrop} ${isFallback ? styles.fallbackAnimation : ''}`}
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${0.5 + Math.random() * 0.5}s`
              }}
            />
          ))}
        </div>
      );
    }, [weatherCondition, isLoading, hasError, isFallback]);

    const snowFlakes = useMemo(() => {
      if (weatherCondition !== 'snow' || isLoading || hasError) return null;
      
      // Reduce animation intensity for fallback data
      const flakeCount = isFallback ? 15 : 30;
      
      return (
        <div className={styles.snowFlakes}>
          {Array.from({ length: flakeCount }, (_, i) => (
            <div
              key={i}
              className={`${styles.snowFlake} ${isFallback ? styles.fallbackAnimation : ''}`}
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 3}s`,
                animationDuration: `${3 + Math.random() * 2}s`
              }}
            />
          ))}
        </div>
      );
    }, [weatherCondition, isLoading, hasError, isFallback]);

    const stars = useMemo(() => {
      if (weatherCondition !== 'clear-night' || isLoading || hasError) return null;
      
      // Reduce animation intensity for fallback data
      const starCount = isFallback ? 10 : 20;
      
      return (
        <div className={styles.stars}>
          {Array.from({ length: starCount }, (_, i) => (
            <div
              key={i}
              className={`${styles.star} ${isFallback ? styles.fallbackAnimation : ''}`}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 60}%`,
                animationDelay: `${Math.random() * 3}s`
              }}
            />
          ))}
        </div>
      );
    }, [weatherCondition, isLoading, hasError, isFallback]);

    return (
      <div className={`${styles.weatherBackground} ${backgroundClass}`}>
        {/* Animated elements for specific weather conditions */}
        {rainDrops}
        {snowFlakes}
        {stars}

        {/* Loading state overlay */}
        {isLoading && (
          <div className={styles.loadingOverlay}>
            <div className={styles.loadingSpinner} />
          </div>
        )}

        {/* Error state overlay */}
        {hasError && (
          <div className={`${styles.errorOverlay} ${styles[`errorOverlay--${errorSeverity}`]}`}>
            <div className={styles.errorIcon}>
              {errorSeverity === 'high' ? '❌' : errorSeverity === 'medium' ? '⚠️' : 'ℹ️'}
            </div>
          </div>
        )}

        {/* Fallback indicator */}
        {isFallback && !hasError && (
          <div className={styles.fallbackIndicator}>
            <div className={styles.fallbackIcon}>🔄</div>
          </div>
        )}
      </div>
    );
  }
);

export default WeatherBackground;